/**
 * The Messages page's time filter: a preset or a custom range, resolved to the pair of ISO instants
 * the API takes.
 *
 * Two shapes live in the URL, deliberately, because they answer different questions:
 *
 * - `?when=24h` — a ROLLING window. It re-resolves against the clock on every render, so the view
 *   keeps meaning "the last 24 hours" tomorrow as well as today, and a shared link shows the reader
 *   *their* last 24 hours.
 * - `?since=…&until=…` — a FIXED window, absolute ISO instants. This is what "custom" writes, and
 *   what a link to a specific incident needs: it must show the same messages next week.
 *
 * Storing a preset as the instants it resolves to today would quietly turn every rolling view into a
 * fixed one the moment it was shared or reloaded, which is the bug this split exists to avoid.
 *
 * The window is half-open, `[since, until)`, matching the backend's `TimeWindow` exactly.
 */

/** The presets the dropdown offers, shortest first. The absence of one means "all time". */
export const PRESETS = ['1h', '24h', '7d', '30d'] as const

export type Preset = (typeof PRESETS)[number]

/** How far back each preset reaches, in milliseconds. */
const SPAN_MS: Record<Preset, number> = {
  '1h': 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
}

export const PRESET_LABELS: Record<Preset, string> = {
  '1h': 'Last hour',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
}

export function isPreset(value: string | undefined): value is Preset {
  return value !== undefined && (PRESETS as readonly string[]).includes(value)
}

/** What the API is asked for. Both absent means no time filter at all. */
export interface Bounds {
  since?: string
  until?: string
}

/** What the URL carries, straight off `useSearchParams`. */
export interface WindowParams {
  when?: string
  since?: string
  until?: string
}

/**
 * Resolve the URL's parameters to the bounds the API takes.
 *
 * `when` WINS over `since`/`until` when both are present rather than merging them: they are two
 * ways of saying the same thing, and a merge would produce a window neither of them describes. An
 * unrecognised `when` is ignored rather than treated as an error — a URL is user input, and the
 * honest fallback for "I don't know that preset" is the unfiltered view, not an empty one.
 */
export function resolveBounds(params: WindowParams, now: Date = new Date()): Bounds {
  if (isPreset(params.when)) {
    // TRUNCATED TO THE MINUTE, and that is not cosmetic. These bounds go into the TanStack Query
    // key; resolved against a raw `new Date()` they would differ on every single render, minting a
    // fresh cache entry and refetching forever. Truncating makes a preset's window stable for a
    // minute at a time, which is far finer than any of the spans on offer and still rolls.
    const minute = Math.floor(now.getTime() / 60_000) * 60_000
    return { since: new Date(minute - SPAN_MS[params.when]).toISOString() }
  }
  const bounds: Bounds = {}
  // Each bound is taken only if it parses, and is then NORMALISED through `toISOString()`. Both
  // halves matter. `Date` is far more permissive than the backend's `Instant.parse` -- it accepts
  // '2026-09' and reads it as midnight UTC on the 1st -- so passing the raw string through would
  // hand the API something it answers with a 400. Re-serialising means whatever we send is always a
  // full ISO-8601 instant, and an unparseable bound is dropped here instead of erroring the page
  // while someone is still typing.
  if (isInstant(params.since)) bounds.since = new Date(params.since as string).toISOString()
  if (isInstant(params.until)) bounds.until = new Date(params.until as string).toISOString()
  // An empty window is the caller's mistake and the API rejects it (400). Dropping it here keeps a
  // mistyped custom range from turning the page into an error state.
  if (bounds.since !== undefined && bounds.until !== undefined && bounds.since >= bounds.until) {
    return {}
  }
  return bounds
}

function isInstant(raw: string | undefined): boolean {
  if (raw === undefined || raw.trim() === '') return false
  return !Number.isNaN(new Date(raw).getTime())
}

/**
 * The value the dropdown should show: a preset, `custom` when a usable explicit range is present,
 * or `all`.
 *
 * Derived from the URL rather than held in component state, so a reload, a shared link and the Back
 * button all agree with the control — the same reason the channel filter lives in the URL.
 */
export function selectedOption(params: WindowParams): Preset | 'custom' | 'all' {
  if (isPreset(params.when)) return params.when
  const bounds = resolveBounds(params)
  if (bounds.since !== undefined || bounds.until !== undefined) return 'custom'
  return 'all'
}

/**
 * `datetime-local` inputs speak local wall-clock with no zone (`2026-09-02T14:00`), and the API
 * speaks UTC instants. This converts one to the other THROUGH the browser's zone, which is what
 * makes "2pm" mean 2pm where the user is sitting.
 */
export function localInputToInstant(local: string): string | undefined {
  if (local.trim() === '') return undefined
  const parsed = new Date(local)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString()
}

/** The inverse, for filling the inputs back in from the URL. Empty string when there is no bound. */
export function instantToLocalInput(instant: string | undefined): string {
  if (instant === undefined || instant.trim() === '') return ''
  const parsed = new Date(instant)
  if (Number.isNaN(parsed.getTime())) return ''
  // toISOString() is UTC; subtracting the offset first makes the sliced wall-clock the LOCAL one.
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 16)
}
